import React, { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const ContactSection = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    captcha: "",
  });

  const [focus, setFocus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { scrollY } = useScroll();
  const glowY = useTransform(scrollY, [0, 600], [0, 120]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.captcha.trim() !== "100") return;

    setLoading(true);
    try {
      await fetch("https://hooks.zapier.com/hooks/catch/26979240/unrorfw/", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setShowModal(true);
      setTimeout(() => {
        window.location.href = "/thank-you";
      }, 1500);
    } finally {
      setLoading(false);
    }
  };

  const spring = { type: "spring", stiffness: 220, damping: 24 };

  return (
    <div className="bg-[#0A0A0F] text-white relative overflow-hidden min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1">
        {/* 🌌 DYNAMIC GLOW FIELD (CONSTRAINED, NO SCROLL) */}
        <motion.div
          style={{ y: glowY }}
          className="absolute inset-0 -z-10 pointer-events-none overflow-hidden"
        >
          {/* Top-left glow */}
          <div className="absolute w-[60vw] max-w-[700px] h-[60vw] max-h-[700px] bg-white/5 blur-[180px] top-0 left-0 sm:-top-[250px] sm:-left-[200px]" />
          {/* Bottom-right glow */}
          <div className="absolute w-[55vw] max-w-[600px] h-[55vw] max-h-[600px] bg-white/3 blur-[180px] bottom-0 right-0 sm:-bottom-[250px] sm:-right-[200px]" />
        </motion.div>

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={spring}
          className="text-center pt-28 pb-10 px-4 sm:px-6"
        >
          <h1 className="text-4xl font-light tracking-tight">
            Let’s build something great
          </h1>
          <p className="text-gray-400 mt-3 text-sm">
            Tell us about your project — we respond within 24 hours
          </p>
        </motion.div>

        {/* CONVERSION LAYOUT */}
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 pb-10">
          {/* LEFT: TRUST + CONTEXT */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={spring}
            className="space-y-6 w-full"
          >
            <div className="bg-white/5 border border-white/10 rounded-3xl p-7 backdrop-blur-2xl">
              <h2 className="text-sm font-medium text-white/90">
                Why reach out
              </h2>
              <div className="mt-4 space-y-3 text-sm text-gray-400">
                <p>• Tailored solar system recommendations</p>
                <p>• Clear guidance before any commitment</p>
                <p>• Residential & commercial expertise</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white/5 to-white/0 border border-white/10 rounded-3xl p-7">
              <h3 className="text-sm font-medium mb-3">Response quality</h3>
              <p className="text-sm text-gray-400">
                Every inquiry is reviewed personally — not automated replies.
              </p>
            </div>

            {/* FOUNDER CARD */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-7">
              <div className="flex items-center gap-4">
                <img
                  alt="Odedeyi Damilare"
                  src="/Assets/Odedeyi.jpeg"
                  className="w-16 h-16 rounded-2xl"
                />
                <div>
                  <p className="text-sm font-medium">Founder</p>
                  <p className="text-xs text-gray-400">Solar Energy Systems</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                Helping homes and businesses transition to clean solar energy
                systems.
              </p>
            </div>
          </motion.div>

          {/* RIGHT: FORM */}
          <motion.form
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={spring}
            onSubmit={handleSubmit}
            className="bg-white/5 border border-white/10 rounded-3xl backdrop-blur-2xl overflow-hidden w-full"
          >
            {[
              { name: "name", label: "Full name" },
              { name: "email", label: "Email address" },
              { name: "phone", label: "Phone (optional)" },
              { name: "subject", label: "Subject" },
            ].map((f) => (
              <div
                key={f.name}
                className="relative border-b border-white/10 px-6 py-4"
              >
                <motion.span
                  animate={{
                    y: focus === f.name || form[f.name] ? -10 : 0,
                    scale: focus === f.name || form[f.name] ? 0.85 : 1,
                    opacity: focus === f.name ? 1 : 0.6,
                  }}
                  transition={spring}
                  className="absolute text-xs text-gray-400 pointer-events-none"
                >
                  {f.label}
                </motion.span>

                <input
                  name={f.name}
                  onChange={handleChange}
                  onFocus={() => setFocus(f.name)}
                  onBlur={() => setFocus(null)}
                  className="w-full bg-transparent pt-5 outline-none text-sm"
                />
              </div>
            ))}

            <div className="px-6 py-5 border-b border-white/10">
              <textarea
                name="message"
                onChange={handleChange}
                onFocus={() => setFocus("message")}
                onBlur={() => setFocus(null)}
                placeholder="Tell us about your project"
                className="w-full bg-transparent outline-none text-sm h-28 resize-none"
              />
            </div>

            <div className="px-6 py-4">
              <input
                name="captcha"
                onChange={handleChange}
                placeholder="Security check: 60 + 40 = ?"
                className="w-full bg-transparent outline-none text-sm"
              />
            </div>

            <div className="p-5">
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.97 }}
                disabled={loading}
                className={`w-full py-3 rounded-2xl text-sm font-medium transition ${
                  loading ? "bg-white/10 text-gray-400" : "bg-white text-black"
                }`}
              >
                {loading ? "Sending..." : "Send message"}
              </motion.button>
            </div>
          </motion.form>
        </div>

        {/* MODAL */}
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-xl z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={spring}
              className="bg-[#111] border border-white/10 rounded-3xl p-6 w-80 text-center"
            >
              <h2 className="text-white text-sm font-medium">
                Message received
              </h2>
              <p className="text-gray-400 text-xs mt-2">
                We’ll respond within 24 hours.
              </p>

              <button
                onClick={() => setShowModal(false)}
                className="mt-5 text-sm text-white/80 hover:text-white"
              >
                Done
              </button>
            </motion.div>
          </motion.div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ContactSection;
